import { Navigate, Outlet, createBrowserRouter } from "react-router-dom";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";

import { useAuth, type AuthGate } from "./lib/auth";
import { AppShell } from "./components/AppShell";
import { parseOperatingMode } from "./lib/types/enums";
import type { Contractor } from "./lib/types/models";
import { Button, ErrorState, FullPageSpinner, Shell } from "@ui/react";
import { RouteError } from "./components/RouteError";

import { LoginScreen } from "./features/auth/LoginScreen";
import { RegisterScreen } from "./features/auth/RegisterScreen";
import { ForgotPasswordScreen } from "./features/auth/ForgotPasswordScreen";
import { VerifyEmailScreen } from "./features/auth/VerifyEmailScreen";
import { ProfileCompletionScreen } from "./features/auth/ProfileCompletionScreen";
import { BankDetailsScreen } from "./features/auth/BankDetailsScreen";
import { WrongAccountScreen } from "./features/auth/WrongAccountScreen";

import { FindJobsScreen } from "./features/jobs/FindJobsScreen";
import { JobDetailsScreen } from "./features/jobs/JobDetailsScreen";
import { ActiveJobsScreen } from "./features/active/ActiveJobsScreen";
import { MessagesScreen } from "./features/chat/MessagesScreen";
import { EarningsScreen } from "./features/earnings/EarningsScreen";
import { NotificationsScreen } from "./features/notifications/NotificationsScreen";
import { ProfileScreen } from "./features/profile/ProfileScreen";
import { EditProfileScreen } from "./features/profile/EditProfileScreen";

import { BookingsInboxScreen } from "./features/bookings/BookingsInboxScreen";
import { BookingDetailScreen } from "./features/bookings/BookingDetailScreen";
import { BookingSettingsHubScreen } from "./features/booking-settings/BookingSettingsHubScreen";
import { ServicesListScreen } from "./features/booking-settings/ServicesListScreen";
import { ServiceEditorScreen } from "./features/booking-settings/ServiceEditorScreen";
import { ExtrasListScreen } from "./features/booking-settings/ExtrasListScreen";
import { ExtraEditorScreen } from "./features/booking-settings/ExtraEditorScreen";
import { AvailabilityScreen } from "./features/booking-settings/AvailabilityScreen";
import { BookingPolicyScreen } from "./features/booking-settings/BookingPolicyScreen";
import { errorMessage } from "./lib/errorMessage";

/** Where a contractor belongs, given how far through onboarding they are. */
const GATE_ROUTES: Record<Exclude<AuthGate, "loading" | "ready" | "unavailable">, string> = {
  signedOut: "/login",
  needsEmailVerification: "/verify-email",
  wrongRole: "/wrong-account",
  needsProfile: "/onboarding/profile",
  needsBankDetails: "/onboarding/bank-details",
};

/**
 * Where a signed-in, fully-onboarded contractor belongs. `OperatingMode` is
 * mutually exclusive, so a bookable contractor has no job feed at all — sending
 * them to /jobs opened every session on a skeleton for a feed they can never
 * have, on a route no tab marks as current. The same expression lights the
 * shell's brand link (see AppShell's `home`).
 */
function homeFor(contractor: Contractor | null): string {
  return parseOperatingMode(contractor?.operatingMode) === "bookable" ? "/bookings" : "/jobs";
}

/** The index route, which cannot be a static `<Navigate>`: home depends on the
 *  contractor, and only a component inside the tree can read them. */
function Home() {
  const { contractor } = useAuth();
  return <Navigate to={homeFor(contractor)} replace />;
}

/**
 * Rendered when the profile could not be read for a reason other than "no
 * record yet". Deliberately NOT a redirect: sending this contractor into the
 * onboarding form would offer them a blank profile whose submit overwrites the
 * real one. A retry is the only safe action.
 *
 * Both guards short-circuit EVERY matched route to this, /login and /register
 * included, so "Try again" alone was a trap: a persistent account-specific
 * failure left no way to end the session. Sign-out is the same escape the other
 * gate screens carry, in the same words and the same tertiary footer.
 */
function ProfileUnavailable() {
  const { error, refresh, signOut } = useAuth();
  return (
    <Shell variant="auth" brandHref="/login">
      <ErrorState
        message={errorMessage(error ?? "We couldn't load your profile.")}
        onRetry={() => void refresh()}
      />
      <div className="auth-note">
        <Button variant="tertiary" onClick={() => void signOut()}>
          Sign out and use a different account
        </Button>
      </div>
    </Shell>
  );
}

/**
 * Routes that only make sense at one specific gate. Sitting on `/verify-email`
 * with a verified address, or on the bank form after the payout account exists,
 * both mean "move along" — which is what makes the funnel feel linear instead of
 * trapping people on a step they have already finished.
 */
function GateRoute({ gate, children }: { gate: Exclude<AuthGate, "loading">; children: ReactNode }) {
  const { gate: current, contractor } = useAuth();

  if (current === "loading") return <FullPageSpinner label="Checking your session" />;
  if (current === "unavailable") return <ProfileUnavailable />;
  if (current === gate) return <>{children}</>;

  // Public auth pages are the one place a fully-onboarded contractor can legally
  // land; send them to the side of the workspace they actually work rather than
  // round-tripping the funnel. Every web sign-in lands here — LoginScreen
  // deliberately does not navigate — so a hardcoded /jobs was the destination of
  // every session a bookable contractor ever started.
  if (current === "ready") return <Navigate to={homeFor(contractor)} replace />;
  return <Navigate to={GATE_ROUTES[current]} replace />;
}

/** Everything behind all four gates. */
function RequireReady() {
  const { gate } = useAuth();

  if (gate === "loading") return <FullPageSpinner label="Loading your workspace" />;
  if (gate === "unavailable") return <ProfileUnavailable />;
  if (gate !== "ready") return <Navigate to={GATE_ROUTES[gate]} replace />;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/* Wrapped so a transaction is named by its MATCHED ROUTE PATTERN
 * (/jobs/:jobId) rather than its URL (/jobs/job_8f21) — otherwise every
 * job, booking and conversation a contractor opens is its own transaction
 * name and nothing aggregates. Pairs with
 * reactRouterV6BrowserTracingIntegration in lib/errors.ts, which supplies the
 * hooks; both halves are required. */
const createRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = createRouter([
  {
    errorElement: <RouteError />,
    children: [
      /* ---- public / onboarding funnel ---- */
      { path: "/login", element: <GateRoute gate="signedOut"><LoginScreen /></GateRoute> },
      { path: "/register", element: <GateRoute gate="signedOut"><RegisterScreen /></GateRoute> },
      {
        path: "/forgot-password",
        element: <GateRoute gate="signedOut"><ForgotPasswordScreen /></GateRoute>,
      },
      {
        path: "/verify-email",
        element: <GateRoute gate="needsEmailVerification"><VerifyEmailScreen /></GateRoute>,
      },
      {
        path: "/wrong-account",
        element: <GateRoute gate="wrongRole"><WrongAccountScreen /></GateRoute>,
      },
      {
        path: "/onboarding/profile",
        element: <GateRoute gate="needsProfile"><ProfileCompletionScreen /></GateRoute>,
      },
      {
        path: "/onboarding/bank-details",
        element: <GateRoute gate="needsBankDetails"><BankDetailsScreen /></GateRoute>,
      },

      /* ---- the workspace ---- */
      {
        element: <RequireReady />,
        children: [
          { index: true, element: <Home /> },
          { path: "/jobs", element: <FindJobsScreen /> },
          { path: "/jobs/:jobId", element: <JobDetailsScreen /> },
          { path: "/active", element: <ActiveJobsScreen /> },

          // One route for both panes: on a wide viewport the inbox and the
          // thread render side by side, on a narrow one the thread replaces the
          // list. Keeping it a single route means a deep link to a conversation
          // works identically at every width.
          { path: "/messages", element: <MessagesScreen /> },
          { path: "/messages/:conversationId", element: <MessagesScreen /> },

          { path: "/earnings", element: <EarningsScreen /> },
          { path: "/notifications", element: <NotificationsScreen /> },
          { path: "/profile", element: <ProfileScreen /> },
          { path: "/profile/edit", element: <EditProfileScreen /> },

          { path: "/bookings", element: <BookingsInboxScreen /> },
          { path: "/bookings/:bookingId", element: <BookingDetailScreen /> },

          { path: "/settings/booking", element: <BookingSettingsHubScreen /> },
          { path: "/settings/services", element: <ServicesListScreen /> },
          { path: "/settings/services/:serviceId", element: <ServiceEditorScreen /> },
          { path: "/settings/extras", element: <ExtrasListScreen /> },
          { path: "/settings/extras/:extraId", element: <ExtraEditorScreen /> },
          { path: "/settings/availability", element: <AvailabilityScreen /> },
          { path: "/settings/booking-policy", element: <BookingPolicyScreen /> },
        ],
      },

      { path: "*", element: <RouteError notFound /> },
    ],
  },
]);
